/**
 * Timezone-aware date/time formatting for the logged-in agent.
 *
 * Generalizes the one-off `formatInTz` pattern that previously lived only in
 * DirectoryPages.tsx (used there for a *contact's* timezone) into a shared
 * utility used everywhere a date/time is rendered to the *viewing agent*.
 * Every call site should pass the current user's `timezone` (from
 * `useAuth().user?.timezone`) rather than relying on implicit browser-local
 * time, since agents across a 14-country desk are not all in the browser's
 * local zone relative to the event they're viewing.
 *
 * Falls back to the browser's own local timezone when no timezone is known
 * yet (e.g. session still loading) rather than throwing, so pages don't need
 * to guard every render with a loading check just for date formatting.
 */

/** date + time, e.g. "12 Aug 2026, 3:45 pm" */
export function formatDateTime(value: string | Date | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

/** date only, e.g. "12 Aug 2026" */
export function formatDate(value: string | Date | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleDateString();
  }
}

/** time only, e.g. "3:45 pm" */
export function formatTime(value: string | Date | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeStyle: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleTimeString();
  }
}
/** relative age, e.g. "17 hours ago" */
export function formatRelativeAge(value: string | Date | null | undefined, now: number = Date.now()): string {
  if (!value) return '—';
  const diffSeconds = Math.round((new Date(value).getTime() - now) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000], ['month', 2592000], ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds) return formatter.format(Math.round(diffSeconds / seconds), unit);
  }
  return formatter.format(0, 'second');
}
/** full stamp, e.g. "Mon, 2 Nov 2026 at 5:58 PM" */
export function formatFullStamp(value: string | Date | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      ...(timezone ? { timeZone: timezone } : {}),
    }).formatToParts(new Date(value));
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    return `${get('weekday')}, ${get('day')} ${get('month')} ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
  } catch {
    return new Date(value).toLocaleString();
  }
}
/** "17 hours ago (Mon, 2 Nov 2026 at 5:58 PM)" */
export function formatRelativeWithDate(value: string | Date | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  return `${formatRelativeAge(value)} (${formatFullStamp(value, timezone)})`;
}
